import React from 'react';
import { Youtube, Play, ExternalLink, Video, GraduationCap, BookOpen, Monitor } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const YouTubeScreen = () => {
  const youtubeChannelUrl = 'https://www.youtube.com/@STBrain-TrainerIMGbR';
  const grokKILernvideosUrl = 'https://lake-drift-giant-tundra.grok.me/code/zugang?mwbv=true';

  const openYouTubeChannel = () => {
    window.open(youtubeChannelUrl, '_blank', 'noopener,noreferrer');
  };

  const openGrokKILernvideos = () => {
    window.open(grokKILernvideosUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-background pb-20 safe-area-top">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-lg border-b border-border">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Video className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">S&T KI-Lernvideos</h1>
              <p className="text-xs text-muted-foreground">Videos & Kurse</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        
        {/* Grok Lernvideos - Premium Section */}
        <Card className="bg-gradient-to-br from-primary/20 to-yellow-900/20 border-primary/30 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-4">
              {/* Grok Logo */}
              <div className="w-20 h-20 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
                <GraduationCap className="w-12 h-12 text-black" />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-xl font-bold text-foreground">Exklusive KI-Lernvideos</h2>
                  <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-semibold rounded-full">
                    Premium
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Detaillierte Kurs-Videos für Industriemeister Prüfungsvorbereitung
                </p>
              </div>

              {/* Main CTA Button */}
              <Button 
                onClick={openGrokKILernvideos}
                className="w-full bg-primary hover:bg-primary/90 text-black font-semibold py-6 text-base shadow-lg shadow-primary/30"
                data-testid="grok-lernvideos-btn"
              >
                <Monitor className="w-5 h-5 mr-2" />
                KI-Lernvideos öffnen
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-xs text-muted-foreground">
                Melde dich mit deiner freigeschalteten E-Mail an
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Info Section - Grok */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Kurs-Inhalte
          </h3>

          <div className="grid gap-3">
            {[
              {
                title: 'Strukturierte Kurse',
                description: 'Aufeinander aufbauende Lerneinheiten',
                icon: Play
              },
              {
                title: 'Dozenten-Videos',
                description: 'Direkt vom Experten erklärt',
                icon: Play
              },
              {
                title: 'Prüfungsrelevant',
                description: 'Fokus auf IHK-Prüfungsinhalte',
                icon: Play
              }
            ].map((item, index) => (
              <Card key={index} className="bg-card/50 border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-border"></div>
          <span className="text-xs text-muted-foreground">Weitere Videos</span>
          <div className="flex-1 h-px bg-border"></div>
        </div>

        {/* YouTube Section */}
        <Card className="bg-gradient-to-br from-red-600/20 to-red-900/20 border-red-600/30 overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/30 flex-shrink-0">
                <Youtube className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-foreground">YouTube-Kanal</h3>
                <p className="text-sm text-muted-foreground truncate">
                  Kostenlose Lernvideos & Tipps
                </p>
              </div>
            </div>

            <Button 
              onClick={openYouTubeChannel}
              className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white font-semibold py-5"
              data-testid="youtube-channel-btn"
            >
              <Youtube className="w-5 h-5 mr-2" />
              S&T YouTube-Kanal öffnen
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Tips */}
        <div className="bg-card/30 rounded-lg p-4 space-y-2">
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Tipp für KI-Lernvideos:</strong> Nutze die E-Mail-Adresse, die für die Videos freigeschaltet wurde.
          </p>
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Tipp für YouTube:</strong> Wenn die YouTube-App installiert ist, öffnet sich der Kanal direkt in der App.
          </p>
        </div>
      </div>
    </div>
  );
};

export default YouTubeScreen;
